/**
 * Table normalisation.
 *
 * Tables reach the editor from three directions: our own toolbar, content saved
 * by earlier versions of the editor, and the clipboard (Word, Google Docs and
 * Sheets, arbitrary web pages). Everything downstream — the generated
 * `<style data-table-style>` blocks, the settings panel, add row/column, the
 * horizontal-scroll wrapper — assumes a table looks the way our own toolbar
 * builds it:
 *
 *   - the table carries `data-table-id` and `data-border-color/width/radius`
 *   - it sits inside `div.table-wrapper[data-for-table][data-table-wrapper]`
 *   - borders live as inline styles on the cells, not on the table
 *   - cells carry `data-column-index`
 *   - the only classes present are the ones we generate
 *
 * A pasted table satisfies none of that, so the toolbar silently no-ops on it.
 * This module brings any table to that shape, which is why it runs on paste, on
 * load and on every update — existing content gets repaired in place.
 *
 * Every operation here is idempotent: normalising an already-normalised table
 * must not change it, otherwise the editor would emit a new value on every
 * keystroke.
 */

/** Class name prefixes this editor generates itself. */
const OWN_CLASS_PREFIXES = [
  "cell-style-",
  "col-style-",
  "table-style-",
  "responsive-table-",
  "responsive-cell-",
  "responsive-style-",
  "responsive-font-",
  "responsive-weight-",
  "responsive-align-",
];

/**
 * Classes that are ours but carry no suffix. `border-collapse` and `w-full`
 * come from CustomTable's configured HTMLAttributes and are re-added by TipTap
 * on every render, so stripping them would only churn the output.
 */
const OWN_CLASS_EXACT = new Set(["table-wrapper", "border-collapse", "w-full"]);

/** Elements whose classes and inline styles are owned by the table system. */
const TABLE_SCOPED_SELECTOR = "table, thead, tbody, tfoot, tr, td, th, col, colgroup, caption";

/**
 * System colour keywords. Word and LibreOffice emit `windowtext`; browsers
 * either render them as an unexpected theme colour or ignore them outright, so
 * they are swapped for a real colour.
 */
const SYSTEM_COLOR_SOURCE =
  "\\b(windowtext|window|buttonface|buttontext|menutext|captiontext|infotext|infobackground|threedface|threedshadow|threedhighlight|activeborder|inactiveborder|windowframe)\\b";
/** Stateless — used for `test`. A `g` flag here would make results alternate. */
const SYSTEM_COLOR_RE = new RegExp(SYSTEM_COLOR_SOURCE, "i");
/** Global — used for `replace`. */
const SYSTEM_COLOR_RE_ALL = new RegExp(SYSTEM_COLOR_SOURCE, "gi");

/** Values that cannot serve as a colour once system keywords are resolved. */
const UNUSABLE_COLORS = new Set([
  "",
  "currentcolor",
  "initial",
  "inherit",
  "unset",
  "revert",
  "auto",
  "none",
]);

/** Absolute CSS length units Word/Docs emit, expressed in px. */
const UNIT_TO_PX: Record<string, number> = {
  px: 1,
  pt: 96 / 72,
  pc: 16,
  in: 96,
  cm: 96 / 2.54,
  mm: 96 / 25.4,
};

const LENGTH_PROPS = /^(width|min-width|max-width|height|min-height|max-height|padding|margin|font-size)/;

const BORDER_SIDES = ["top", "right", "bottom", "left"] as const;
type BorderSide = (typeof BORDER_SIDES)[number];

const LINE_STYLE_RE = /^(none|hidden|solid|dashed|dotted|double|groove|ridge|inset|outset)$/i;
const LINE_WIDTH_RE = /^(-?[\d.]+[a-z%]*|thin|medium|thick)$/i;

export interface TableBorder {
  color: string;
  width: number;
}

export const DEFAULT_BORDER_COLOR = "#000000";

const isOwnClass = (cls: string): boolean =>
  OWN_CLASS_EXACT.has(cls) || OWN_CLASS_PREFIXES.some((prefix) => cls.startsWith(prefix));

const isUsableColor = (value: string | null | undefined): boolean =>
  !!value && !UNUSABLE_COLORS.has(value.trim().toLowerCase()) && !SYSTEM_COLOR_RE.test(value);

/**
 * Convert a CSS length to a pixel number. Returns null for percentages,
 * relative units and anything unparseable — those are left untouched rather
 * than guessed at.
 */
const lengthToPx = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const match = value.trim().match(/^(-?[\d.]+)\s*([a-z%]*)$/i);
  if (!match) return null;
  const amount = parseFloat(match[1]);
  if (isNaN(amount)) return null;
  const factor = UNIT_TO_PX[(match[2] || "px").toLowerCase()];
  if (factor === undefined) return null;
  return Math.round(amount * factor * 100) / 100;
};

/** Border widths are always whole pixels; a sub-pixel border disappears. */
const toBorderWidthPx = (value: string | null | undefined): number | null => {
  if (value && /^(thin|medium|thick)$/i.test(value.trim())) {
    return { thin: 1, medium: 3, thick: 5 }[value.trim().toLowerCase() as "thin" | "medium" | "thick"];
  }
  const px = lengthToPx(value);
  if (px === null) return null;
  return px > 0 ? Math.max(1, Math.round(px)) : 0;
};

/**
 * Class names that some stylesheet in the document actually targets. Content
 * authored through the HTML source view can carry hand-written classes with
 * matching CSS, and those are not "foreign" — dropping them would break the
 * author's own styling.
 */
const collectStyledClassNames = (cssSources: string[]): Set<string> => {
  const names = new Set<string>();
  cssSources.forEach((css) => {
    if (!css) return;
    const matches = css.matchAll(/\.(-?[_a-zA-Z][\w-]*)/g);
    for (const match of matches) names.add(match[1]);
  });
  return names;
};

/** Strip every class this editor did not generate and no stylesheet targets. */
const keepOwnClasses = (el: Element, styledClasses: Set<string>): void => {
  const current = el.getAttribute("class");
  if (!current) return;
  const all = current.split(/\s+/).filter(Boolean);
  const kept = all.filter((cls) => isOwnClass(cls) || styledClasses.has(cls));
  if (kept.length === all.length) return;
  if (kept.length > 0) {
    el.setAttribute("class", kept.join(" "));
  } else {
    el.removeAttribute("class");
  }
};

/** Split a `border` / `border-<side>` shorthand into its three components. */
const parseBorderShorthand = (value: string) => {
  let width: string | null = null;
  let lineStyle: string | null = null;
  let color: string | null = null;
  value
    .trim()
    .split(/\s+/)
    .forEach((part) => {
      if (LINE_STYLE_RE.test(part)) lineStyle = part.toLowerCase();
      else if (LINE_WIDTH_RE.test(part)) width = part;
      else color = part;
    });
  return { width, lineStyle, color };
};

/**
 * Rewrite a foreign inline style into something our CSS model can work with:
 * drop `mso-*`, resolve system colours, convert absolute non-px lengths
 * (Word's `120.0pt`) to px, and expand per-side border shorthands into the
 * longhands the rest of the pipeline reads.
 */
const normalizeInlineStyle = (el: HTMLElement): void => {
  const style = el.getAttribute("style");
  if (!style) return;

  const out: string[] = [];
  const push = (prop: string, value: string) => out.push(`${prop}: ${value}`);

  style.split(";").forEach((raw) => {
    const decl = raw.trim();
    if (!decl) return;
    const colon = decl.indexOf(":");
    if (colon === -1) return;

    const prop = decl.slice(0, colon).trim().toLowerCase();
    let value = decl.slice(colon + 1).trim();
    if (!prop || !value) return;
    if (prop.startsWith("mso-")) return;

    // Resolve system colour keywords to a real colour so the declaration renders.
    if (SYSTEM_COLOR_RE.test(value)) {
      value = value.replace(SYSTEM_COLOR_RE_ALL, DEFAULT_BORDER_COLOR);
    }

    if ((prop === "color" || prop === "background-color") && !isUsableColor(value)) return;

    // `border` / `border-top` … carry all three components at once; the rest of
    // the pipeline reads colour, style and width separately.
    const sideMatch = prop.match(/^border(?:-(top|right|bottom|left))?$/);
    if (sideMatch) {
      const { width, lineStyle, color } = parseBorderShorthand(value);
      const sides: BorderSide[] = sideMatch[1] ? [sideMatch[1] as BorderSide] : [...BORDER_SIDES];
      sides.forEach((side) => {
        if (lineStyle) push(`border-${side}-style`, lineStyle);
        if (width !== null) {
          const px = toBorderWidthPx(width);
          push(`border-${side}-width`, px === null ? width : `${px}px`);
        }
        if (color && isUsableColor(color)) push(`border-${side}-color`, color);
      });
      return;
    }

    if (/^border(-(top|right|bottom|left))?-width$/.test(prop)) {
      const converted = value
        .split(/\s+/)
        .map((part) => {
          const px = toBorderWidthPx(part);
          return px === null ? part : `${px}px`;
        })
        .join(" ");
      push(prop, converted);
      return;
    }

    if (LENGTH_PROPS.test(prop)) {
      value = value
        .split(/\s+/)
        .map((part) => {
          if (!/^-?[\d.]+(pt|pc|in|cm|mm)$/i.test(part)) return part;
          const px = lengthToPx(part);
          return px === null ? part : `${px}px`;
        })
        .join(" ");
    }

    push(prop, value);
  });

  const next = out.join("; ");
  const previous = style
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean)
    .join("; ");
  if (next === previous) return;
  if (next) {
    el.setAttribute("style", next);
  } else {
    el.removeAttribute("style");
  }
};

/**
 * The table's authoritative border. `data-border-*` wins because that is what
 * the settings panel writes; otherwise fall back to whatever the cells already
 * show, so a pasted table keeps the look it arrived with. Returns null when the
 * table has no border at all — a borderless table stays borderless.
 */
export const readTableBorder = (table: Element): TableBorder | null => {
  const attrColor = table.getAttribute("data-border-color");
  const attrWidth = table.getAttribute("data-border-width");
  const parsedWidth = attrWidth === null ? NaN : parseFloat(attrWidth);

  let color = isUsableColor(attrColor) ? (attrColor as string) : "";
  let width = isNaN(parsedWidth) ? NaN : parsedWidth;
  if (color && !isNaN(width)) return { color, width };

  const cells = table.querySelectorAll("td, th");
  for (let i = 0; i < cells.length; i++) {
    const cellStyle = (cells[i] as HTMLElement).style;
    if (!color) {
      const candidate =
        cellStyle.borderRightColor || cellStyle.borderBottomColor || cellStyle.borderColor;
      if (isUsableColor(candidate)) color = candidate;
    }
    if (isNaN(width)) {
      const candidate = toBorderWidthPx(cellStyle.borderRightWidth || cellStyle.borderBottomWidth);
      if (candidate !== null && candidate > 0) width = candidate;
    }
    if (color && !isNaN(width)) break;
  }

  if (!color && isNaN(width)) return null;
  return { color: color || DEFAULT_BORDER_COLOR, width: isNaN(width) ? 1 : width };
};

/**
 * The 4-value `border-width` a cell needs so adjacent cells do not draw two
 * lines on top of each other. With a row gap the rows stand apart, so every row
 * keeps its own top border.
 */
export const cellBorderWidthValue = (
  borderWidth: number,
  isFirstRow: boolean,
  isFirstCol: boolean,
  hasRowGap = false,
): string => {
  const top = isFirstRow || hasRowGap ? borderWidth : 0;
  const left = isFirstCol ? borderWidth : 0;
  return `${top}px ${borderWidth}px ${borderWidth}px ${left}px`;
};

/** True when the cell carries its own cell-level styling and owns its borders. */
export const hasOwnCellStyling = (cell: Element): boolean =>
  cell.hasAttribute("data-cell-id") ||
  cell.hasAttribute("data-cell-class") ||
  cell.hasAttribute("data-cell-settings") ||
  /\bcell-style-\w+/.test(cell.getAttribute("class") || "");

/** Row gap in px from a `data-width-xs` compact string ("type:value:align:scroll:gap"). */
export const parseRowGap = (widthXs: string | null | undefined): number => {
  if (!widthXs) return 0;
  const gap = parseInt(widthXs.split(":")[4] || "0", 10);
  return isNaN(gap) ? 0 : gap;
};

/** Row gap in px, read back from the table's `data-width-xs` compact string. */
export const readRowGap = (table: Element): number => parseRowGap(table.getAttribute("data-width-xs"));

/** Give the table a stable id. Index-based, so repeat runs do not churn ids. */
const ensureTableId = (table: Element, index: number, taken: Set<string>): string => {
  const existing = table.getAttribute("data-table-id");
  if (existing) return existing;
  let id = `table-auto-${index}`;
  let suffix = 0;
  while (taken.has(id)) id = `table-auto-${index}-${++suffix}`;
  table.setAttribute("data-table-id", id);
  return id;
};

/** Put the table inside the wrapper the scroll and alignment CSS is scoped to. */
const ensureWrapper = (table: Element, tableId: string): void => {
  const parent = table.parentElement;
  if (parent?.classList.contains("table-wrapper")) {
    if (parent.getAttribute("data-for-table") !== tableId) {
      parent.setAttribute("data-for-table", tableId);
    }
    if (parent.getAttribute("data-table-wrapper") !== "true") {
      parent.setAttribute("data-table-wrapper", "true");
    }
    if (!parent.getAttribute("style")) {
      parent.setAttribute("style", "width: 100%; max-width: 100%;");
    }
    return;
  }

  const doc = table.ownerDocument;
  if (!doc || !table.parentNode) return;
  const wrapper = doc.createElement("div");
  wrapper.className = "table-wrapper";
  wrapper.setAttribute("data-for-table", tableId);
  wrapper.setAttribute("data-table-wrapper", "true");
  wrapper.setAttribute("style", "width: 100%; max-width: 100%;");
  table.parentNode.insertBefore(wrapper, table);
  wrapper.appendChild(table);
};

/** Presentational HTML attributes from Word/Docs that fight our CSS. */
const TABLE_LEGACY_ATTRS = ["border", "cellspacing", "cellpadding", "bgcolor", "align", "width", "height"];
const CELL_LEGACY_ATTRS = ["bgcolor", "align", "valign", "width", "height", "border"];

/**
 * Bring one table to the shape the rest of the editor expects.
 * Returns true when anything changed.
 */
const normalizeTable = (
  table: HTMLTableElement,
  index: number,
  taken: Set<string>,
  styledClasses: Set<string>,
): boolean => {
  const before = table.outerHTML;
  const wrapperBefore = table.parentElement?.classList.contains("table-wrapper")
    ? table.parentElement.outerHTML
    : "";

  const tableId = ensureTableId(table, index, taken);
  taken.add(tableId);

  // 1. Strip foreign classes and inline junk from the table and everything in it.
  keepOwnClasses(table, styledClasses);
  normalizeInlineStyle(table);
  table.querySelectorAll(TABLE_SCOPED_SELECTOR).forEach((el) => {
    keepOwnClasses(el, styledClasses);
    normalizeInlineStyle(el as HTMLElement);
  });

  TABLE_LEGACY_ATTRS.forEach((attr) => table.removeAttribute(attr));
  table.querySelectorAll("tr, td, th").forEach((el) => {
    CELL_LEGACY_ATTRS.forEach((attr) => el.removeAttribute(attr));
  });

  // TipTap writes col widths from `colwidth`; they override responsive column CSS.
  table.querySelectorAll("col").forEach((col) => {
    const colEl = col as HTMLElement;
    colEl.style.removeProperty("width");
    colEl.style.removeProperty("min-width");
    colEl.style.removeProperty("max-width");
    if (!colEl.getAttribute("style")) colEl.removeAttribute("style");
  });

  // 2. Table-level settings, derived from the cells when absent.
  const border = readTableBorder(table);
  if (border) {
    if (table.getAttribute("data-border-color") !== border.color) {
      table.setAttribute("data-border-color", border.color);
    }
    if (table.getAttribute("data-border-width") !== String(border.width)) {
      table.setAttribute("data-border-width", String(border.width));
    }
  }
  if (!table.getAttribute("data-border-radius")) table.setAttribute("data-border-radius", "0");
  if (!table.getAttribute("data-width-xs")) table.setAttribute("data-width-xs", "percent:100:left:0:");

  // Borders belong to the cells; a border on the table itself doubles them up.
  table.style.border = "none";
  table.style.borderCollapse = "separate";

  // 3. Per-cell normalisation: column index, plus a border for every cell that
  //    does not carry its own cell-level styling. This is what repairs the
  //    empty-looking cells TipTap creates for a new row or column.
  const rowGap = readRowGap(table);
  const rows = Array.from(table.querySelectorAll("tr"));
  rows.forEach((row, rowIndex) => {
    Array.from(row.querySelectorAll("td, th")).forEach((cell, colIndex) => {
      const cellEl = cell as HTMLElement;

      if (cellEl.getAttribute("data-column-index") !== String(colIndex)) {
        cellEl.setAttribute("data-column-index", String(colIndex));
      }

      if (!border || hasOwnCellStyling(cellEl)) return;

      const widthValue = cellBorderWidthValue(border.width, rowIndex === 0, colIndex === 0, rowGap > 0);
      if (cellEl.style.borderColor !== border.color) cellEl.style.borderColor = border.color;
      if (cellEl.style.borderStyle !== "solid") cellEl.style.borderStyle = "solid";
      if (cellEl.style.borderWidth !== widthValue) cellEl.style.borderWidth = widthValue;
    });
  });

  // 4. The wrapper the scroll and alignment CSS is scoped to.
  ensureWrapper(table, tableId);

  const wrapperAfter = table.parentElement?.classList.contains("table-wrapper")
    ? table.parentElement.outerHTML
    : "";
  return table.outerHTML !== before || wrapperAfter !== wrapperBefore;
};

/**
 * Normalise every table under `root`. Returns true when anything changed, so
 * callers can skip re-serialising untouched content.
 */
export const normalizeTablesInDocument = (
  root: ParentNode,
  options: { cssSource?: string } = {},
): boolean => {
  const tables = Array.from(root.querySelectorAll("table")) as HTMLTableElement[];
  if (tables.length === 0) return false;

  const taken = new Set<string>();
  tables.forEach((t) => {
    const id = t.getAttribute("data-table-id");
    if (id) taken.add(id);
  });

  // `cssSource` covers callers that stripped the <style> tags before parsing.
  const inlineCss = Array.from(root.querySelectorAll("style")).map((s) => s.textContent || "");
  const styledClasses = collectStyledClassNames([...inlineCss, options.cssSource || ""]);

  let changed = false;
  tables.forEach((table, index) => {
    if (normalizeTable(table, index, taken, styledClasses)) changed = true;
  });
  return changed;
};

/**
 * String-in/string-out wrapper around {@link normalizeTablesInDocument}.
 *
 * DOMParser hoists leading `<style>` tags into `<head>`, which would silently
 * drop the generated table CSS, so the result is discarded if the round-trip
 * lost a style tag.
 */
export const normalizeTablesInHTML = (html: string): string => {
  if (!html || !html.includes("<table")) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  if (!normalizeTablesInDocument(doc.body, { cssSource: html })) return html;

  const result = doc.body.innerHTML;
  const styleCount = (input: string) => (input.match(/<style[\s>]/gi) || []).length;
  return styleCount(result) === styleCount(html) ? result : html;
};
